import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Camera, SwitchCamera, RotateCcw, Check, ScanLine } from 'lucide';
import { lucideIcon } from '../icon.js';
import { maakBarcodeLezer, maakStabieleLezing } from '../utils/barcode-lezer.js';

/**
 * <camera-venster> — de camera van de computer of tablet, voor een foto of een barcode.
 *
 *   modus="foto"     maak een foto → 'foto' { file }
 *   modus="barcode"  scan een barcode → 'barcode' { code }
 *
 * Zoals in de veldapp: de barcode wordt pas geaccepteerd als hij stabiel gelezen is (zie
 * barcode-lezer.js), en een barcode die al op deze boring zit wordt geweigerd (`duplicaatVan`).
 * Heeft het apparaat meer dan één camera (laptop met webcam en een losse camera, of een
 * Windows-tablet met voor- en achtercamera), dan staat er een knop om te wisselen. We beginnen
 * met de achtercamera als die er is: daarmee richt je op een potje of een grondlaag.
 *
 * Sluiten → 'sluit'. De camera gaat uit zodra het venster dicht is.
 */
export class CameraVenster extends LitElement {
  static properties = {
    open: { type: Boolean },
    modus: { type: String },          // 'foto' | 'barcode'
    titel: { type: String },
    /** (code) => naam van het monster/de fles waar de code al op zit, of null. */
    duplicaatVan: { attribute: false },
    _fase: { state: true },           // 'starten' | 'live' | 'genomen' | 'gescand' | 'fout'
    _fout: { state: true },
    _cameras: { state: true },        // [{ deviceId, label }]
    _cameraId: { state: true },
    _foto: { state: true },           // { file, url }
    _code: { state: true },
  };

  constructor() {
    super();
    this.open = false;
    this.modus = 'foto';
    this.titel = '';
    this.duplicaatVan = null;
    this._fase = 'starten';
    this._fout = '';
    this._cameras = [];
    this._cameraId = null;
    this._foto = null;
    this._code = null;
    this._stream = null;
    this._lezing = maakStabieleLezing();
    this._scanTimer = null;
    this._onKey = (e) => { if (e.key === 'Escape' && this.open) this._sluit(); };
  }

  connectedCallback() { super.connectedCallback(); window.addEventListener('keydown', this._onKey); }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._onKey);
    this._stop();
    this._wisFoto();
  }

  updated(changed) {
    if (changed.has('open')) {
      if (this.open) this._start();
      else { this._stop(); this._wisFoto(); }
    }
  }

  get _video() { return this.renderRoot.querySelector('video'); }

  // ─── Camera ──────────────────────────────────────────────────────────────

  async _start(cameraId = null) {
    this._stop();
    this._fout = '';
    this._fase = 'starten';
    this._code = null;
    this._lezing.reset();
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this._fase = 'fout';
      this._fout = 'De camera werkt alleen via een beveiligde verbinding (https).';
      return;
    }
    const video = cameraId
      ? { deviceId: { exact: cameraId } }
      // Achtercamera als die er is; op een laptop is er alleen de webcam en kiest de browser die.
      : { facingMode: { ideal: 'environment' } };
    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        video: { ...video, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
    } catch (e) {
      this._fase = 'fout';
      this._fout = this._foutTekst(e);
      return;
    }
    if (!this.open) { this._stop(); return; } // venster ging dicht terwijl de browser nog vroeg
    this._cameraId = this._stream.getVideoTracks()[0]?.getSettings?.().deviceId ?? cameraId;
    // Pas na toestemming geeft de browser de namen en de volledige lijst van camera's.
    try {
      const apparaten = await navigator.mediaDevices.enumerateDevices();
      this._cameras = apparaten.filter((d) => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
    } catch { this._cameras = []; }
    this._fase = 'live';
    await this.updateComplete;
    const v = this._video;
    if (!v) return;
    v.srcObject = this._stream;
    try { await v.play(); } catch { /* autoplay van een stille video mag altijd; anders start hij bij de volgende render */ }
    if (this.modus === 'barcode') this._scan();
  }

  _stop() {
    clearTimeout(this._scanTimer);
    this._scanTimer = null;
    this._stream?.getTracks().forEach((t) => t.stop());
    this._stream = null;
    const v = this._video;
    if (v) v.srcObject = null;
  }

  _foutTekst(e) {
    switch (e?.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'Geen toegang tot de camera. Sta de camera toe via het slotje links in de adresbalk en probeer het opnieuw.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'Geen camera gevonden op dit apparaat.';
      case 'NotReadableError':
        return 'De camera is in gebruik door een ander programma (bijvoorbeeld Teams of Zoom). Sluit dat en probeer het opnieuw.';
      default:
        return 'De camera kon niet worden gestart.';
    }
  }

  /** Naar de volgende camera in de lijst. */
  _wissel() {
    if (this._cameras.length < 2) return;
    const i = this._cameras.findIndex((c) => c.deviceId === this._cameraId);
    const volgende = this._cameras[(i + 1) % this._cameras.length];
    this._start(volgende.deviceId);
  }

  // ─── Foto ────────────────────────────────────────────────────────────────

  async _neemFoto() {
    const v = this._video;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0);
    const blob = await new Promise((ok) => canvas.toBlob(ok, 'image/jpeg', 0.9));
    if (!blob) return;
    const stempel = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const file = new File([blob], `camera_${stempel}.jpg`, { type: 'image/jpeg' });
    this._wisFoto();
    this._foto = { file, url: URL.createObjectURL(blob) };
    this._fase = 'genomen';
    this._stop(); // camera uit terwijl je de foto bekijkt; 'Opnieuw' zet hem weer aan
  }

  _wisFoto() {
    if (this._foto?.url) URL.revokeObjectURL(this._foto.url);
    this._foto = null;
  }

  _gebruikFoto() {
    const file = this._foto?.file;
    if (!file) return;
    this.dispatchEvent(new CustomEvent('foto', { detail: { file }, bubbles: true, composed: true }));
    this._sluit();
  }

  // ─── Barcode ─────────────────────────────────────────────────────────────

  async _scan() {
    let lezer;
    try {
      lezer = await maakBarcodeLezer();
    } catch {
      this._fase = 'fout';
      this._fout = 'De barcodelezer kon niet worden geladen. Controleer de internetverbinding en probeer het opnieuw.';
      this._stop();
      return;
    }
    const stap = async () => {
      if (!this._stream || this._fase !== 'live') return;
      const v = this._video;
      if (v && v.readyState >= 2) {
        try {
          const gevonden = await lezer.detect(v);
          const code = this._lezing.lees(gevonden[0]?.rawValue || '');
          if (code) { this._gescand(code); return; }
        } catch { /* een enkel beeld dat niet te lezen is: volgende */ }
      }
      this._scanTimer = setTimeout(stap, 67);
    };
    stap();
  }

  _gescand(code) {
    this._code = code;
    this._fase = 'gescand';
    clearTimeout(this._scanTimer);
    // Zelfde signaal als een handscanner: kort piepje en (op een tablet) trillen.
    try { navigator.vibrate?.(80); } catch { /* geen trilfunctie */ }
    this._piep();
  }

  _piep() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 1800;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
      osc.onended = () => ctx.close();
    } catch { /* geen geluid: niet erg */ }
  }

  _opnieuw() {
    this._wisFoto();
    this._code = null;
    this._lezing.reset();
    if (this._stream) { this._fase = 'live'; if (this.modus === 'barcode') this._scan(); }
    else this._start(this._cameraId);
  }

  _gebruikCode() {
    if (!this._code || this._duplicaat) return;
    this.dispatchEvent(new CustomEvent('barcode', { detail: { code: this._code }, bubbles: true, composed: true }));
    this._sluit();
  }

  get _duplicaat() {
    return this._code && typeof this.duplicaatVan === 'function' ? this.duplicaatVan(this._code) : null;
  }

  _sluit() {
    this._stop();
    this._wisFoto();
    this.dispatchEvent(new CustomEvent('sluit', { bubbles: true, composed: true }));
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  render() {
    if (!this.open) return html``;
    const barcode = this.modus === 'barcode';
    const titel = this.titel || (barcode ? 'Barcode scannen' : 'Foto maken');
    const kanWisselen = this._cameras.length > 1 && (this._fase === 'live' || this._fase === 'starten');
    return html`
      <div class="overlay" @click=${(e) => e.target === e.currentTarget && this._sluit()}>
        <div class="kaart" role="dialog" aria-label=${titel}>
          <div class="kop">
            <h3>${titel}</h3>
            <button class="icoon" @click=${this._sluit} aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 20))}</button>
          </div>

          <div class="beeld">
            ${this._fase === 'fout' ? html`
              <div class="melding">
                ${unsafeSVG(lucideIcon(Camera, 34))}
                <p>${this._fout}</p>
                <button class="secundair" @click=${() => this._start(this._cameraId)}>${unsafeSVG(lucideIcon(RotateCcw, 15))} Opnieuw proberen</button>
              </div>
            ` : this._fase === 'genomen' && this._foto ? html`
              <img src=${this._foto.url} alt="Gemaakte foto">
            ` : html`
              <video playsinline muted autoplay></video>
              ${this._fase === 'starten' ? html`<div class="laag"><div class="draaier"></div><span>Camera starten…</span></div>` : ''}
              ${barcode && this._fase === 'live' ? html`<div class="richtkader"><span></span></div>` : ''}
              ${barcode && this._fase === 'gescand' ? this._renderGescand() : ''}
            `}
            ${kanWisselen ? html`
              <button class="wissel" @click=${this._wissel} title="Wissel van camera" aria-label="Wissel van camera">
                ${unsafeSVG(lucideIcon(SwitchCamera, 20))}
              </button>` : ''}
          </div>

          <div class="voet">
            ${this._fase === 'genomen' ? html`
              <button class="secundair" @click=${this._opnieuw}>${unsafeSVG(lucideIcon(RotateCcw, 15))} Opnieuw</button>
              <button class="primair" @click=${this._gebruikFoto}>${unsafeSVG(lucideIcon(Check, 15))} Foto gebruiken</button>
            ` : this._fase === 'gescand' ? html`
              <button class="secundair" @click=${this._opnieuw}>${unsafeSVG(lucideIcon(RotateCcw, 15))} Opnieuw scannen</button>
              ${this._duplicaat ? '' : html`<button class="primair" @click=${this._gebruikCode}>${unsafeSVG(lucideIcon(Check, 15))} Barcode gebruiken</button>`}
            ` : barcode ? html`
              <span class="hint">${unsafeSVG(lucideIcon(ScanLine, 16))} Houd de barcode stil in beeld</span>
            ` : html`
              <button class="sluiter" ?disabled=${this._fase !== 'live'} @click=${this._neemFoto} aria-label="Foto maken">
                ${unsafeSVG(lucideIcon(Camera, 22))}
              </button>
            `}
          </div>
          ${kanWisselen && this._cameras.length ? html`
            <div class="camera-naam">${this._cameras.find((c) => c.deviceId === this._cameraId)?.label || ''}</div>` : ''}
        </div>
      </div>
    `;
  }

  _renderGescand() {
    const dubbel = this._duplicaat;
    return html`
      <div class="laag ${dubbel ? 'dubbel' : 'goed'}">
        <div class="resultaat">
          <div class="titel">${dubbel ? 'Deze barcode is al gebruikt' : 'Barcode gescand!'}</div>
          <div class="code">${this._code}</div>
          ${dubbel ? html`<div class="uitleg">Hij zit al op ${dubbel}. Scan een ander potje, of pas het bestaande monster aan.</div>` : ''}
        </div>
      </div>`;
  }

  static styles = css`
    :host { display: contents; }
    .overlay {
      position: fixed; inset: 0; z-index: 1000; background: rgba(15, 23, 42, 0.55);
      display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .kaart {
      width: 100%; max-width: 760px; max-height: calc(100vh - 32px); background: var(--mv-surface, #fff);
      border-radius: 16px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); padding: 22px 24px;
      display: flex; flex-direction: column; gap: 14px; box-sizing: border-box; color: var(--mv-text);
    }
    .kop { display: flex; align-items: center; justify-content: space-between; }
    .kop h3 { margin: 0; font-size: 1.15rem; }
    .icoon { border: 1px solid var(--mv-border); background: var(--mv-surface, #fff); border-radius: 50%; width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--mv-text); }
    .beeld {
      position: relative; background: #000; border-radius: 12px; overflow: hidden;
      aspect-ratio: 16 / 10; min-height: 0; display: flex; align-items: center; justify-content: center;
    }
    video, img { width: 100%; height: 100%; object-fit: contain; display: block; }
    .laag { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #fff; background: rgba(0,0,0,0.45); }
    .laag.goed { background: rgba(22, 101, 52, 0.82); }
    .laag.dubbel { background: rgba(153, 27, 27, 0.85); }
    .resultaat { text-align: center; padding: 0 24px; }
    .resultaat .titel { font-weight: 800; font-size: 1.1rem; }
    .resultaat .code { font-family: ui-monospace, monospace; font-size: 1.6rem; font-weight: 800; margin-top: 8px; word-break: break-all; }
    .resultaat .uitleg { margin-top: 10px; font-size: 0.9rem; opacity: 0.9; }
    .richtkader { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; }
    .richtkader span { width: 62%; height: 38%; border: 3px solid rgba(255,255,255,0.9); border-radius: 14px; box-shadow: 0 0 0 9999px rgba(0,0,0,0.25); }
    .melding { color: #e5e7eb; text-align: center; padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 12px; max-width: 460px; }
    .melding p { margin: 0; line-height: 1.45; }
    .wissel {
      position: absolute; top: 12px; right: 12px; width: 44px; height: 44px; border-radius: 50%;
      border: none; background: rgba(0,0,0,0.55); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
    }
    .wissel:hover { background: rgba(0,0,0,0.75); }
    .voet { display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 64px; }
    .sluiter {
      width: 64px; height: 64px; border-radius: 50%; border: 4px solid var(--mv-primary); background: var(--mv-primary);
      color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: inset 0 0 0 3px #fff;
    }
    .sluiter:disabled { opacity: 0.4; cursor: default; }
    .primair, .secundair {
      display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; border-radius: 8px;
      font: inherit; font-weight: 600; font-size: 0.9rem; cursor: pointer;
    }
    .primair { background: var(--mv-primary); color: #fff; border: 1px solid var(--mv-primary); }
    .secundair { background: var(--mv-surface, #fff); color: var(--mv-text); border: 1px solid var(--mv-border); }
    .hint { display: inline-flex; align-items: center; gap: 6px; color: var(--mv-text-muted); font-size: 0.9rem; }
    .camera-naam { text-align: center; font-size: 0.78rem; color: var(--mv-text-muted); margin-top: -6px; }
    .draaier { width: 34px; height: 34px; border: 3px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: draai 0.9s linear infinite; }
    @keyframes draai { to { transform: rotate(360deg); } }
  `;
}

customElements.define('camera-venster', CameraVenster);
